import { useEffect, useMemo, useState, MouseEvent } from "react";
import { useMutation } from "react-query";
import { getPatientJourney, getPatientJourneyOfEncounters } from "../../EMR/services";
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Paper, 
  useTheme,
  alpha,
  Popover,
  Stack,
  Divider
} from "@mui/material";
import moment from "moment";
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { PatientType } from ".";

interface JourneyItem {
  id: number;
  source: string;
  destination: string;
  sourceDateTime: string;
  destinationDateTime: string;
  timeDuration: string;
  encounterId?: number;
}

const statusColors: any = {
  "Booked": "#3498db",
  "Booking Confirmed": "#2ecc71",
  "Registered": "#9b59b6",
  "Arrived": "#f1c40f",
  "Encountered": "#e67e22",
  "Emr Started": "#1abc9c",
  "Emr Submitted": "#27ae60",
  "Billed": "#34495e",
  "Cashed": "#d35400",
  "default": "#7f8c8d"
};

const PatientJourney = ({
  patient: _patient,
  encounterId,
  encounterIds,
}: {
  patient: PatientType;
  encounterId?: number | null;
  encounterIds?: number[];
}) => {
  const theme = useTheme();
  const { data, isLoading, mutate } = useMutation(getPatientJourney);
  const {
    data: allJourneyData,
    isLoading: isAllJourneyLoading,
    mutate: mutateAllJourney,
  } = useMutation(getPatientJourneyOfEncounters);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeStep, setActiveStep] = useState<JourneyItem | null>(null);

  useEffect(() => {
    if (encounterId) {
      mutate(encounterId);
    }
  }, [encounterId, mutate]);

  useEffect(() => {
    if (encounterIds?.length) {
      mutateAllJourney(encounterIds);
    }
  }, [encounterIds, mutateAllJourney]);

  const isAllMode = Boolean(encounterIds?.length);
  const activeJourneyData = isAllMode ? allJourneyData : data;
  const rawJourney = useMemo(() => {
    if (isAllMode) {
      const encounterJourneys = Array.isArray(activeJourneyData?.response)
        ? activeJourneyData.response
        : [];
      return encounterJourneys.flatMap((encounterItem: any) =>
        encounterItem?.journey?.journeyDetails?.journey?.map((step: any) => ({
          ...step,
          encounterId: encounterItem?.encounterId,
        })) || [],
      );
    }
    return (
      activeJourneyData?.response?.journeyDetails?.journey ||
      activeJourneyData?.response?.journey ||
      activeJourneyData?.response ||
      []
    );
  }, [isAllMode, activeJourneyData]);
  const journey: JourneyItem[] = useMemo(() => {
    if (!Array.isArray(rawJourney)) return [];
    return rawJourney.map((step: any, index: number) => ({
      id: Number(step?.id ?? index + 1),
      source: String(step?.source || step?.from || "-"),
      destination: String(step?.destination || step?.to || step?.status || "-"),
      sourceDateTime: String(step?.sourceDateTime || step?.fromDateTime || ""),
      destinationDateTime: String(
        step?.destinationDateTime || step?.toDateTime || step?.date || "",
      ),
      timeDuration: String(step?.timeDuration || step?.duration || ""),
    }));
  }, [rawJourney]);
  const groupedJourneyByDate = useMemo(() => {
    if (!isAllMode) return [] as { date: string; steps: JourneyItem[] }[];
    const map = new Map<string, JourneyItem[]>();
    journey.forEach((step) => {
      const normalizedDate = step.destinationDateTime
        ? moment(step.destinationDateTime).format("YYYY-MM-DD")
        : "Unknown";
      if (!map.has(normalizedDate)) map.set(normalizedDate, []);
      map.get(normalizedDate)?.push(step);
    });
    return Array.from(map.entries())
      .map(([date, steps]) => ({
        date,
        steps: steps.sort(
          (a, b) =>
            moment(a.destinationDateTime).valueOf() -
            moment(b.destinationDateTime).valueOf(),
        ),
      }))
      .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
  }, [journey, isAllMode]);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>, step: JourneyItem) => {
    setAnchorEl(event.currentTarget);
    setActiveStep(step);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setActiveStep(null);
  };

  const open = Boolean(anchorEl);
  const emptyJourneyMessage =
    activeJourneyData?.responseMessage || "Patient journey has not started yet.";

  if (isLoading || isAllJourneyLoading) return (
    <Box display="flex" justifyContent="center" p={2}>
      <CircularProgress size={24} thickness={4} />
    </Box>
  );

  if (!journey.length) return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 2,
        borderRadius: "12px",
        bgcolor: "#ffffff",
        border: "1px solid #eef2f6",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {emptyJourneyMessage}
      </Typography>
    </Paper>
  );

  const nodeWidth = 140;
  const highY = 25;
  const lowY = 85;

  const generatePath = (steps: JourneyItem[]) => {
    let d = `M ${nodeWidth/2} ${highY}`;
    for (let i = 1; i < steps.length; i++) {
      const prevX = (i - 1) * nodeWidth + nodeWidth/2;
      const currX = i * nodeWidth + nodeWidth/2;
      const prevY = (i - 1) % 2 === 0 ? highY : lowY;
      const currY = i % 2 === 0 ? highY : lowY;
      const cpX = (prevX + currX) / 2;
      d += ` Q ${cpX} ${prevY}, ${currX} ${currY}`;
    }
    return d;
  };
  const renderJourneyTrack = (steps: JourneyItem[]) => (
    <Box sx={{ 
      width: '100%',
      overflowX: 'auto', 
      pb: 1,
      pt: 1,
      px: 1,
      '&::-webkit-scrollbar': { height: '6px' },
      '&::-webkit-scrollbar-track': { background: '#f8fafc' },
      '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '10px' },
      '&::-webkit-scrollbar-thumb:hover': { background: '#94a3b8' },
    }}>
      <Box sx={{ 
          display: 'block', 
          minWidth: steps.length * nodeWidth, 
          position: 'relative', 
          height: '110px',
          overflow: 'hidden'
      }}>
        <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
          <path 
              d={generatePath(steps)} 
              fill="none" 
              stroke="#cbd5e1" 
              strokeWidth="2" 
              strokeDasharray="5,5"
          />
        </svg>
        {steps.map((step: JourneyItem, index: number) => {
          const color = statusColors[step.destination] || statusColors.default;
          const isEven = index % 2 === 0;
          const xPos = index * nodeWidth;
          const yPos = isEven ? highY - 10 : lowY - 10;
          const encounterSuffix = step?.encounterId ? `#${step.encounterId}` : "";
          
          return (
            <Box 
              key={`${step.id}-${step.destinationDateTime}-${index}`} 
              onMouseEnter={(e) => handlePopoverOpen(e, step)}
              onMouseLeave={handlePopoverClose}
              sx={{ 
                  position: 'absolute', 
                  left: xPos, 
                  top: yPos,
                  width: nodeWidth,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  zIndex: 2,
                  cursor: 'pointer'
              }}
            >
              <Box sx={{ 
                width: 20, 
                height: 20, 
                borderRadius: '50%', 
                border: `2.5px solid ${color}`,
                bgcolor: 'white',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                boxShadow: `0 0 10px ${alpha(color, 0.3)}`,
              }}>
                <FiberManualRecordIcon sx={{ fontSize: 9, color: color }} />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.6rem', color: color, mt: 0.5 }}>
                {String(step.destination || "-").split(" ").slice(-1)} {encounterSuffix}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: "12px", bgcolor: "#ffffff", border: "1px solid #eef2f6", boxShadow: "0 2px 10px rgba(0,0,0,0.02)", maxWidth: "79dvw" }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Box sx={{ p: 0.5, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1, display: 'flex' }}>
          <AutoGraphIcon sx={{ fontSize: 18 }} color="primary" />
        </Box>
        <Box>
            <Typography variant="body2" fontWeight="800" color="text.primary" sx={{ lineHeight: 1 }}>Patient Journey Map</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Journey tracking {activeJourneyData?.response?.journeyDetails?.timeTakenForConsultation && `| Time taken for consultation ${activeJourneyData?.response?.journeyDetails?.timeTakenForConsultation || "-"}`}  {activeJourneyData?.response?.journeyDetails?.waitingTime && `| Waiting time ${activeJourneyData?.response?.journeyDetails?.waitingTime || "-"}`}
              </Typography>
        </Box>
      </Box>

      {isAllMode ? (
        <Stack spacing={1.5}>
          {groupedJourneyByDate.map((group) => (
            <Paper key={group.date} elevation={0} sx={{ p: 1, border: "1px solid #eef2f6" }}>
              <Typography variant="caption" fontWeight={800} color="primary.main" sx={{ pl: 1 }}>
                {group.date !== "Unknown" ? moment(group.date).format("DD-MM-YYYY") : "Unknown Date"}
              </Typography>
              {renderJourneyTrack(group.steps)}
            </Paper>
          ))}
        </Stack>
      ) : (
        renderJourneyTrack(journey)
      )}

      {/* Popover for step details */}
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
        PaperProps={{
            elevation: 3,
            sx: {
                p: 1.5,
                borderRadius: '10px',
                border: `1px solid ${activeStep ? alpha(statusColors[activeStep.destination] || statusColors.default, 0.2) : '#eee'}`,
                bgcolor: 'white',
                minWidth: '150px'
            }
        }}
      >
        {activeStep && (
          <Box>
            <Typography variant="subtitle2" fontWeight="800" sx={{ color: statusColors[activeStep.destination] || statusColors.default, mb: 0.5 }}>
              {activeStep.destination}
            </Typography>
            <Divider sx={{ mb: 1, opacity: 0.5 }} />
            <Stack spacing={0.5}>
                <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="caption" color="text.secondary">Time:</Typography>
                    <Typography variant="caption" fontWeight="700">{moment(activeStep.destinationDateTime).format("hh:mm A")}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="caption" color="text.secondary">Date:</Typography>
                    <Typography variant="caption" fontWeight="700">{moment(activeStep.destinationDateTime).format("DD MMM YYYY")}</Typography>
                </Box>
                {activeStep.timeDuration !== "0d:00h:00m:00s" && (
                    <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography variant="caption" color="text.secondary">Duration:</Typography>
                        <Typography variant="caption" fontWeight="700" color="primary.main">{activeStep.timeDuration}</Typography>
                    </Box>
                )}
            </Stack>
          </Box>
        )}
      </Popover>
    </Paper>
  );
};

export default PatientJourney;
